import {
  LTM_EBITDA_PREMIUM_ALIAS,
  LTM_REVENUE_PREMIUM_ALIAS,
  MULTIPLE_TYPE_EBITDA_SELECTOR,
  MULTIPLE_TYPE_REVENUE_SELECTOR,
  NTM_EBITDA_PREMIUM_ALIAS,
  NTM_REVENUE_PREMIUM_ALIAS,
} from 'common/constants/calibration/calibration';
import { NOT_APPLICABLE } from 'common/constants/general';
import {
  oneDecimalPercentFormatValidateFloatTrue,
  percentileFormat,
  xStandardSuffixFormat,
} from 'common/formats/formats';
import { Rows, RowWithOrder } from 'common/types/scalarSpreadsheet';
import { alphabetGenerator } from '../../../../../utilities/alphabet-utilities';
import MultipleTypeSelect from '../../guidelinePublicCompanies/gpc/MultipleTypeSelect';
import MultipleTypeValueViewer from '../../guidelinePublicCompanies/gpc/MultipleTypeValueViewer';
import { RowConfigOutputsParams } from '../performance_metrics/config/rowConfig/types';
import { getCorrespondingFormula } from '../utilities';
import { getCalibratedKeys, getPerformanceMetricsKeys } from '../utils';

const rowConfigCalibrationOutputs = (params: RowConfigOutputsParams): Rows => {
  const { calibration, columnsPerformance, isDisabled } = params;
  const { panelId, performances } = calibration;

  const updatedPanelId = panelId.replaceAll('-', '_');

  const performanceMetricsKeys = getPerformanceMetricsKeys(columnsPerformance, 'performance_metrics');
  const rankRowOffset = performances?.length ? performances.length + 3 : 3;

  const calibratedPerformanceMetricsLtmKeys = getCalibratedKeys(
    performanceMetricsKeys,
    updatedPanelId,
    'performance_metrics_ltm_revenue_multiple'
  );

  const alphabet = alphabetGenerator([], columnsPerformance?.length);
  const performanceMetricsColumnKeys = {
    ltmRevenue: 2,
    ntmRevenue: 5,
    ltmEbitda: 8,
    ntmEbitda: 10,
  };
  const getPerformanceMetricRankExpr = (colNum: number) =>
    `=calibration_performance_${updatedPanelId}.${alphabet[colNum]}${rankRowOffset}`;

  const calibratedPerformanceMetricsNtmKeys = getCalibratedKeys(
    performanceMetricsKeys,
    updatedPanelId,
    'performance_metrics_ntm_revenue_multiple'
  );

  const calibratedPerformanceMetricsLtmEbitdaKeys = getCalibratedKeys(
    performanceMetricsKeys,
    updatedPanelId,
    'performance_metrics_ltm_ebitda_multiple'
  );

  const calibratedPerformanceMetricsNtmEbitdaKeys = getCalibratedKeys(
    performanceMetricsKeys,
    updatedPanelId,
    'performance_metrics_ntm_ebitda_multiple'
  );

  const rows = [
    {
      alias: 'outputs_header',
      className: '',
      readOnly: true,
      value: 'Calibrated Outputs',
      gridType: 'string',
      rowSpan: 1,
      ignoreRowCopy: true,
      isTitleOrHeader: true,
      // order: 1,
    } as RowWithOrder,
    {
      alias: 'ltm_revenue_multiple_outputs',
      readOnly: true,
      value: 'LTM Revenue Multiple',
      gridType: 'number',
      format: xStandardSuffixFormat,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      expr: `=IF(calibration_inputs_${updatedPanelId}.ltm_revenue_inputs == 0, "${NOT_APPLICABLE}", (calibration_inputs_${updatedPanelId}.enterprise_value_inputs / calibration_inputs_${updatedPanelId}.ltm_revenue_inputs))`,
      order: 2,
    } as RowWithOrder,
    {
      alias: 'ntm_revenue_multiple_outputs',
      readOnly: true,
      value: 'NTM Revenue Multiple',
      gridType: 'number',
      format: xStandardSuffixFormat,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      expr: `=IF(calibration_inputs_${updatedPanelId}.ntm_revenue_inputs == 0, "${NOT_APPLICABLE}", calibration_inputs_${updatedPanelId}.enterprise_value_inputs / calibration_inputs_${updatedPanelId}.ntm_revenue_inputs)`,
      order: 3,
    } as RowWithOrder,
    {
      alias: 'ltm_revenue_multiple_percentile',
      readOnly: true,
      value: 'LTM Revenue Multiple Percentile',
      gridType: 'number',
      format: percentileFormat,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      expr: getPerformanceMetricRankExpr(performanceMetricsColumnKeys.ltmRevenue),
      order: 4,
    } as RowWithOrder,
    {
      alias: 'ntm_revenue_multiple_percentile',
      readOnly: true,
      value: 'NTM Revenue Multiple Percentile',
      gridType: 'number',
      format: percentileFormat,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      expr: getPerformanceMetricRankExpr(performanceMetricsColumnKeys.ntmRevenue),
      order: 5,
    } as RowWithOrder,
    {
      alias: MULTIPLE_TYPE_REVENUE_SELECTOR,
      readOnly: isDisabled,
      dataEditor: props => <MultipleTypeSelect shouldIgnorePercentiles={false} shouldIgnoreSpecified {...props} />,
      valueViewer: props => (
        <MultipleTypeValueViewer shouldIgnorePercentiles={false} shouldIgnoreSpecified {...props} />
      ),
      value: 'Multiple Type',
      order: 6,
    } as RowWithOrder,
    {
      alias: 'empty',
      readOnly: true,
      value: '',
      ignoreRowCopy: true,
      isClassNameRequired: true,
      className: 'row-invisible',
      order: 9,
    } as RowWithOrder,
    {
      alias: 'ltm_ebitda_multiple_outputs',
      readOnly: true,
      value: 'LTM EBITDA Multiple',
      gridType: 'number',
      format: xStandardSuffixFormat,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      expr: `=IF(calibration_inputs_${updatedPanelId}.ltm_ebitda_inputs == 0, "${NOT_APPLICABLE}", calibration_inputs_${updatedPanelId}.enterprise_value_inputs / calibration_inputs_${updatedPanelId}.ltm_ebitda_inputs)`,
      order: 10,
    } as RowWithOrder,
    {
      alias: 'ntm_ebitda_multiple_outputs',
      readOnly: true,
      value: 'NTM EBITDA Multiple',
      gridType: 'number',
      format: xStandardSuffixFormat,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      expr: `=IF(calibration_inputs_${updatedPanelId}.ntm_ebitda_inputs == 0,"${NOT_APPLICABLE}", calibration_inputs_${updatedPanelId}.enterprise_value_inputs / calibration_inputs_${updatedPanelId}.ntm_ebitda_inputs)`,
      order: 11,
    } as RowWithOrder,
    {
      alias: 'ltm_ebitda_multiple_percentile',
      readOnly: true,
      value: 'LTM EBITDA Multiple Percentile',
      gridType: 'number',
      format: percentileFormat,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      expr: getPerformanceMetricRankExpr(performanceMetricsColumnKeys.ltmEbitda),
      order: 12,
    } as RowWithOrder,
    {
      alias: 'ntm_ebitda_multiple_percentile',
      readOnly: true,
      value: 'NTM EBITDA Multiple Percentile',
      gridType: 'number',
      format: percentileFormat,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      expr: getPerformanceMetricRankExpr(performanceMetricsColumnKeys.ntmEbitda),
      order: 13,
    } as RowWithOrder,
    {
      alias: MULTIPLE_TYPE_EBITDA_SELECTOR,
      readOnly: isDisabled,
      dataEditor: props => <MultipleTypeSelect shouldIgnorePercentiles={false} shouldIgnoreSpecified {...props} />,
      valueViewer: props => (
        <MultipleTypeValueViewer shouldIgnorePercentiles={false} shouldIgnoreSpecified {...props} />
      ),
      value: 'Multiple Type',
      order: 14,
    } as RowWithOrder,
  ];

  const rowsMedian = [
    {
      alias: LTM_REVENUE_PREMIUM_ALIAS,
      readOnly: true,
      value: 'LTM Revenue Multiple Premium',
      gridType: 'number',
      format: oneDecimalPercentFormatValidateFloatTrue,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      expr: getCorrespondingFormula(
        calibration?.revenue_premium_formula_selector,
        'ltm',
        'revenue',
        calibratedPerformanceMetricsLtmKeys
      ),
      order: 7,
    } as RowWithOrder,
    {
      alias: NTM_REVENUE_PREMIUM_ALIAS,
      readOnly: true,
      value: 'NTM Revenue Multiple Premium',
      gridType: 'number',
      format: oneDecimalPercentFormatValidateFloatTrue,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      order: 8,
      expr: getCorrespondingFormula(
        calibration?.revenue_premium_formula_selector,
        'ntm',
        'revenue',
        calibratedPerformanceMetricsNtmKeys
      ),
    } as RowWithOrder,
    {
      alias: LTM_EBITDA_PREMIUM_ALIAS,
      readOnly: true,
      value: 'LTM EBITDA Multiple Premium',
      gridType: 'number',
      format: oneDecimalPercentFormatValidateFloatTrue,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      order: 15,
      expr: getCorrespondingFormula(
        calibration?.ebitda_premium_formula_selector,
        'ltm',
        'ebitda',
        calibratedPerformanceMetricsLtmEbitdaKeys
      ),
    } as RowWithOrder,
    {
      alias: NTM_EBITDA_PREMIUM_ALIAS,
      readOnly: true,
      value: 'NTM EBITDA Multiple Premium',
      gridType: 'number',
      format: oneDecimalPercentFormatValidateFloatTrue,
      ignoreRowCopy: true,
      isClassNameRequired: true,
      order: 16,
      expr: getCorrespondingFormula(
        calibration?.ebitda_premium_formula_selector,
        'ntm',
        'ebitda',
        calibratedPerformanceMetricsNtmEbitdaKeys
      ),
    } as RowWithOrder,
  ];

  const allRows = [...rows, ...rowsMedian];

  allRows.sort((a, b) => a.order - b.order);

  const originalRows: Rows = allRows.map(({ order, ...rest }) => rest);

  return originalRows;
};

export default rowConfigCalibrationOutputs;
